import { observer } from "mobx-react-lite";
import React, { useState } from "react";

import {
  Card,
  FontWeights,
  IPivotProps,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  Spinner,
  Stack
} from "@bps/fluent-ui";
import { XeroFetchedAccountDto } from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { AccountMapping } from "@stores/custom-integration/models/AccountMapping.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { useIntegrationScreenContext } from "../context/IntegrationScreenContext.ts";
import { XeroAccountMappingForm } from "./XeroAccountMappingForm.tsx";
import { XeroSyncRequestStatusMessageBar } from "./XeroSyncRequestStatusMessageBar.tsx";

enum PivotKeys {
  Defaults = "1",
  ProviderDetails = "2"
}

export const XeroAccountMappingCard: React.FC = observer(() => {
  const [pivotKey, setPivotKey] = useState<string>(PivotKeys.Defaults);
  const [formValid, setFormValid] = useState(true);

  const { xeroTenantId, showXeroConfigurationCard, xeroTenants } =
    useIntegrationScreenContext();

  if (!xeroTenantId || !showXeroConfigurationCard || xeroTenants.length === 0)
    return null;

  const changePivot: IPivotProps["onLinkClick"] = async item => {
    if (!item?.props.itemKey) return;
    setPivotKey(item.props.itemKey);
  };

  return (
    <Card
      heading="Account mapping"
      iconName="Bank"
      styles={{
        heading: { fontSize: 20, fontWeight: FontWeights.semibold },
        subHeading: { fontSize: 12 },
        subComponentStyles: {
          tile: {
            content: { padding: 24 }
          }
        }
      }}
    >
      <Stack
        horizontal
        horizontalAlign="space-between"
        tokens={{ childrenGap: 16 }}
      >
        <Stack grow verticalAlign="start" tokens={{ childrenGap: 4 }}>
          <XeroSyncRequestStatusMessageBar />
          {!formValid && (
            <MessageBar messageBarType={MessageBarType.warning}>
              All default transaction types must be mapped to a Xero account
              before syncing is enabled
            </MessageBar>
          )}
          <Pivot selectedKey={pivotKey} onLinkClick={changePivot}>
            <PivotItem itemKey={PivotKeys.Defaults} headerText="Defaults">
              <DataFetcher<[XeroFetchedAccountDto[], AccountMapping[]]>
                fallback={<Spinner />}
                refetchId={xeroTenantId}
                fetch={async root => {
                  return await Promise.all([
                    root.customIntegrationXero.getFetchedAccount(),
                    root.customIntegrationXero.getAccountMappings()
                  ]);
                }}
              >
                {([fetchedAccountData, accountMappings]) => (
                  <XeroAccountMappingForm
                    accountMappings={accountMappings[0]}
                    fetchedAccountData={fetchedAccountData}
                    onValidityChange={setFormValid}
                  />
                )}
              </DataFetcher>
            </PivotItem>

            <PivotItem
              itemKey={PivotKeys.ProviderDetails}
              headerText="Providers"
            />
          </Pivot>
        </Stack>
      </Stack>
    </Card>
  );
});
